import { FeedOrderUpdate } from '@tradingblock/types';
import Config from '../../config';
import { FeedActions, getFeedActions } from './FeedState';
import _ from 'lodash';
import { OrderFeedActions } from '../global/actions/OrderFeedActions';
import { NotificationActions } from '../global/actions';

// OrderStatusWebSocketService.ts
class OrderStatusWebSocketService {
  private static instance: OrderStatusWebSocketService;
  private ws: WebSocket | null = null;

  private constructor() {}

  // Singleton instance access
  public static getInstance(): OrderStatusWebSocketService {
    if (!OrderStatusWebSocketService.instance) {
      OrderStatusWebSocketService.instance = new OrderStatusWebSocketService();
      console.log('new websocket instance created');
    }
    return OrderStatusWebSocketService.instance;
  }

  // Initialize WebSocket connection
  public async connect(dispatch: React.Dispatch<FeedActions>, dispatcher: any, globalDispatch: any, apiToken: string) {
    try {
      if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
        this.ws = new WebSocket(Config.orderStatusWebsockets + '?token=' + apiToken);

        function camelToPascalCase(str: string): string {
          return str.charAt(0).toUpperCase() + str.slice(1);
        }
        function convertKeysToPascalCase(obj: any): any {
          if (Array.isArray(obj)) {
            return obj.map(convertKeysToPascalCase);
          } else if (obj !== null && typeof obj === 'object') {
            return Object.entries(obj).reduce(
              (acc, [key, value]) => {
                acc[camelToPascalCase(key)] = convertKeysToPascalCase(value);
                return acc;
              },
              {} as any
            );
          }
          return obj;
        }

        this.ws.onopen = () => {
          dispatcher.connected();
          getFeedActions(dispatch).setOrderStatus('open');
          globalDispatch(
            NotificationActions.hide({
              status: 'Error',
              title: 'Order Status WebSocket Error',
              message: '',
              global: false,
              time: new Date(),
              key: 'order-status-feed-error',
              read: false,
              hide: false,
              viewed: false,
            })
          );
          globalDispatch(
            NotificationActions.read({
              status: 'Error',
              title: 'Order Status WebSocket Error',
              message: '',
              global: false,
              time: new Date(),
              key: 'order-status-feed-error',
              read: false,
              hide: false,
              viewed: false,
            })
          );
        };

        this.ws.onmessage = (ev: MessageEvent) => {
          const jsonVal = ev.data;
          const orderVal: FeedOrderUpdate = convertKeysToPascalCase(JSON.parse(jsonVal));
          getFeedActions(dispatch).onOrderUpdate(orderVal, jsonVal);
          globalDispatch(OrderFeedActions.receivedUpdate({ order: orderVal }));
        };

        this.ws.onclose = (ev: CloseEvent) => {
          if (ev.reason) {
            getFeedActions(dispatch).setOrderStatusCloseReason(ev.reason);
            console.warn(ev.reason);
          }
          getFeedActions(dispatch).setOrderStatus('closed');
          globalDispatch(
            NotificationActions.add({
              status: 'Error',
              title: 'Order Status WebSocket Error',
              message: ev.reason ? ev.reason : 'Order Status Websocket error',
              global: false,
              time: new Date(),
              key: 'order-status-feed-error',
              read: false,
              hide: false,
              viewed: false,
            })
          );
        };

        this.ws.onerror = error => {
          dispatcher.errored({ error: error, reason: 'Unable to connect', message: 'Datafeed error' });
          getFeedActions(dispatch).setOrderStatus('error');
          console.error('Order Status WebSocket error:', error);
          globalDispatch(
            NotificationActions.add({
              status: 'Error',
              title: 'Order Status WebSocket Error',
              message: 'Order Status Websocket error',
              global: false,
              time: new Date(),
              key: 'order-status-feed-error',
              read: false,
              hide: false,
              viewed: false,
            })
          );
        };
      }
    } catch (createWebsocketError) {
      dispatcher.errored({
        error: createWebsocketError,
        reason: 'Error creating order status websocket',
        message: 'Order Status Websocket error',
      });
    }
  }
  // Send message to WebSocket
  public sendMessage(data: string | ArrayBufferLike | Blob | ArrayBufferView) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    } else if (this.ws) {
      console.warn('trying to send message to closed/closing websocket', data);
    }
  }

  public shouldTryReconnect = () => {
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      return true;
    }
    return false;
  };

  // Close WebSocket connection
  public closeConnection() {
    if (this.ws) {
      this.ws.close();
    }
  }
}

export const orderStatusWebSocketService = OrderStatusWebSocketService.getInstance();
